import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  signal,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { SiaAiTextButtonComponent } from '../../ai-prompt/ai-text-button/ai-text-button.component';
import {
  EnvironmentService,
  SiaTooltipDirective,
  SiaTranslatePipe,
} from '@sia/shared';
import { SiaBaseEditorSignalComponent } from '../sia-base-editor/sia-base-editor-signal.component';
import { SiaErrorHintComponent } from '../sia-error-hint/sia-error-hint.component';

@Component({
  selector: 'sia-textarea-box',
  templateUrl: './sia-textarea-box.component.html',
  styleUrls: ['./sia-textarea-box.component.scss'],
  standalone: true,
  imports: [
    SiaTranslatePipe,
    MatFormField,
    MatLabel,
    MatInput,
    FormsModule,
    SiaTooltipDirective,
    MatHint,
    SiaErrorHintComponent,
    SiaAiTextButtonComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SiaTextareaBoxComponent
  extends SiaBaseEditorSignalComponent
  implements AfterViewInit
{
  readonly textAreaMinHeight = input<number | string>(100);
  readonly textAreaMaxHeightVh = input<number | string>('100px');
  readonly autoAdjustHeight = input<boolean>(false);
  readonly enableTypingAnimation = input<boolean>(true);
  readonly forceDisableAI = input<boolean>(false);
  readonly environmentService = inject(EnvironmentService);

  readonly normalizedMinHeight = computed(() =>
    this.#normalizeWithUnit(this.textAreaMinHeight(), '100px', 'px'),
  );
  readonly normalizedMaxHeight = computed(() =>
    this.#normalizeWithUnit(this.textAreaMaxHeightVh(), '100px', 'vh'),
  );

  readonly isFixedHeight = computed(() => {
    return this.normalizedMinHeight() === this.normalizedMaxHeight();
  });

  readonly isTyping = signal(false);
  readonly promptLoading = signal(false);

  constructor() {
    super();
  }

  #normalizeWithUnit(
    value: string | number | undefined | null,
    defaultValue: string,
    unit: string,
  ): string {
    if (value === undefined || value === null || value === '') {
      return defaultValue;
    }
    const val = String(value).trim().replace(/\s+/g, '');
    return /^\d+(\.\d+)?$/.test(val) ? `${val}${unit}` : val;
  }

  @ViewChild('siaTextarea') textareaRef?: ElementRef<HTMLTextAreaElement>;
  private pendingAutoResize: boolean = false;

  ngAfterViewInit(): void {
    if (!this.autoAdjustHeight()) return;
    if (this.pendingAutoResize) {
      queueMicrotask(() => this.autoResize());
      this.pendingAutoResize = false;
    } else {
      queueMicrotask(() => this.autoResize());
    }
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  onPromptLoading(loading: boolean): void {
    this.promptLoading.set(loading);
  }

  /**
   * Override hook from base to auto-resize whenever value programmatically changes
   */
  protected override onInternalValueSet(_newValue: any): void {
    if (this.autoAdjustHeight()) {
      this.requestAutoResize();
    }
  }

  requestAutoResize(): void {
    if (!this.autoAdjustHeight()) return;
    if (this.textareaRef?.nativeElement) {
      // Use a microtask to ensure value binding already applied
      queueMicrotask(() => this.autoResize());
    } else {
      this.pendingAutoResize = true;
    }
  }

  private autoResize(): void {
    if (!this.autoAdjustHeight()) return;
    const el = this.textareaRef?.nativeElement;
    if (!el) return;
    // Reset to auto to correctly measure new scrollHeight
    el.style.height = 'auto';
    const borderBoxAdjustment = 2; // small safety offset

    const targetHeight = el.scrollHeight + borderBoxAdjustment;
    // Apply height - it will be capped by max-height set in the template
    el.style.height = targetHeight + 'px';

    // Check if we reached max height to toggle scrollbar
    if (el.scrollHeight > el.offsetHeight) {
      el.style.overflowY = 'auto';
    } else {
      el.style.overflowY = 'hidden';
    }
  }

}
